import React from 'react';
import { Button } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { MenuItem, SelectActionMenu } from '@web-config-app/core-react-ui';
import {
  useBulkActionsReporting,
  useBulkAction,
  useBulkActionsForBaseId,
  BULK_PROMOTE_ALL_ACTION,
} from '@web-config-app/core-react';
import type { BulkActionMenuItem } from '@web-config-app/core-react';
import type {
  BulkActionOperationType,
  DomainEntity,
  EnvironmentKey,
} from '@web-config-app/core';
import { BulkActionReportingModalContainer } from '../bulk-action-reporting-modal/bulk-action-reporting-modal.container';
import { CopyAllConfirmationModal } from './copy-all-confirmation-modal.component';
import { BulkDeepPublishModalContainer } from '../bulk-publish-container/bulk-deep-publish-modal.container';

interface EntityListBulkActionSelectProps {
  entityName: string;
  isUsingConfigAppBackend: boolean;
  domainEntity: DomainEntity;
  environment: EnvironmentKey;
}
/**
 * EntityListBulkActionSelect component displays a dropdown menu to select an action to perform on a list of entities.
 * @param EntityListBulkActionSelectProps - Properties for the EntityListBulkActionSelect component.
 * @param entityName - The name of the entity.
 * @param isUsingConfigAppBackend - Whether the current environment is using the config app backend.
 * @returns A dropdown menu to select an action to perform on a list of entities.
 */
export const EntityListBulkActionSelect: React.FC<
  EntityListBulkActionSelectProps
> = ({ entityName, isUsingConfigAppBackend, domainEntity, environment }) => {
  const { formatMessage } = useIntl();

  const {
    reportingSession,
    cancelActiveOperation,
    retryActiveOperation,
    activeReportingOperation,
    setActiveReportingOperation,
    setOperationStartedAt,
    resetBulkActionReportingState,
    isBulkPublishOpen,
    setIsBulkPublishOpen,
    bulkPublishSelectedEntities,
    bulkActionReportingStore,
  } = useBulkActionsReporting();

  // The promote action awaiting confirmation, rather than a bare "is the modal open" flag:
  // with one action per target it is also the record of which target was clicked.
  const [pendingCopyAllAction, setPendingCopyAllAction] = React.useState<
    BulkActionMenuItem | undefined
  >(undefined);

  const retryBulkPublishRef = React.useRef<(() => void) | undefined>();
  const cancelBulkPublishRef = React.useRef<(() => void) | undefined>();
  const resetBulkPublishRef = React.useRef<(() => void) | undefined>();

  const exportAllEntitiesAction = useBulkAction('export-all-entities');
  const importAllEntitiesAction = useBulkAction('import-all-entities');
  const clearAllEntitiesImportMemoryAction = useBulkAction(
    'clear-all-entities-import-memory',
  );

  // One per promotion target, rather than a single action for "the other environment" --
  // a tenant with several preview environments has no single other one.
  const bulkPromoteAllEntitiesActions = useBulkActionsForBaseId(
    BULK_PROMOTE_ALL_ACTION,
  );

  const wrapActionWithReportingTracking = React.useCallback(
    (
      baseAction: MenuItem | undefined,
      operationType: BulkActionOperationType,
    ): MenuItem | undefined => {
      if (!baseAction) return undefined;
      return {
        ...baseAction,
        action: () => {
          setActiveReportingOperation(operationType);
          setOperationStartedAt(Date.now());
          baseAction.action?.();
        },
      };
    },
    [setActiveReportingOperation, setOperationStartedAt],
  );

  const exportActionWithReportingModal = React.useMemo(
    () => wrapActionWithReportingTracking(exportAllEntitiesAction, 'export'),
    [wrapActionWithReportingTracking, exportAllEntitiesAction],
  );

  const importActionWithReportingModal = React.useMemo(
    () => wrapActionWithReportingTracking(importAllEntitiesAction, 'import'),
    [wrapActionWithReportingTracking, importAllEntitiesAction],
  );

  // Each target's menu item opens the include-drafts confirmation modal, and the modal has
  // to name -- and later run against -- the target that was actually clicked, so the click
  // stores the action rather than just a boolean "open".
  const copyAllActionsWithReportingModal: MenuItem[] = React.useMemo(
    () =>
      bulkPromoteAllEntitiesActions.map((promoteAction) => ({
        ...promoteAction,
        action: () => setPendingCopyAllAction(promoteAction),
      })),
    [bulkPromoteAllEntitiesActions],
  );

  const actions: MenuItem[] = React.useMemo(
    () =>
      [
        exportActionWithReportingModal,
        importActionWithReportingModal,
        clearAllEntitiesImportMemoryAction,
        ...copyAllActionsWithReportingModal,
      ].filter((item): item is MenuItem => item !== undefined),
    [
      exportActionWithReportingModal,
      importActionWithReportingModal,
      clearAllEntitiesImportMemoryAction,
      copyAllActionsWithReportingModal,
    ],
  );

  const menuGroups = React.useMemo(() => [{ menuItems: actions }], [actions]);

  const hasMenuItems = (menuGroups[0]?.menuItems.length ?? 0) > 0;
  const showMenu = isUsingConfigAppBackend && hasMenuItems;
  const showReportingModal = Boolean(
    reportingSession && activeReportingOperation !== null,
  );

  const handleCopyAllConfirm = React.useCallback(
    (includeDrafts: boolean) => {
      // Runs the action the modal was opened for, so the copy lands in the target named on
      // the modal even if the menu has since rebuilt.
      const confirmedAction = pendingCopyAllAction;
      setPendingCopyAllAction(undefined);
      setActiveReportingOperation('copyToEnvironment');
      setOperationStartedAt(Date.now());
      confirmedAction?.action?.({
        status: includeDrafts ? ['draft', 'published'] : 'published',
      });
    },
    [pendingCopyAllAction, setActiveReportingOperation, setOperationStartedAt],
  );

  const handleModalOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        resetBulkActionReportingState();
      }
    },
    [resetBulkActionReportingState],
  );

  return (
    <>
      {showMenu && (
        <SelectActionMenu menuGroups={menuGroups}>
          <Button
            icon="interfaceMeatballs"
            hideLabel
            priority="tertiary"
            size="medium"
          >
            {formatMessage({ id: 'BULK_ENTITY_ACTIONS' }, { entityName })}
          </Button>
        </SelectActionMenu>
      )}

      {pendingCopyAllAction?.target && (
        <CopyAllConfirmationModal
          open
          onClose={() => setPendingCopyAllAction(undefined)}
          onConfirm={handleCopyAllConfirm}
          entityName={entityName}
          environmentName={pendingCopyAllAction.target.name}
        />
      )}

      {showReportingModal && reportingSession && activeReportingOperation && (
        <BulkActionReportingModalContainer
          open
          session={reportingSession}
          onOpenChange={handleModalOpenChange}
          onCancelCTA={() => {
            cancelActiveOperation?.();
            cancelBulkPublishRef.current?.();
            resetBulkPublishRef.current?.();
            resetBulkActionReportingState();
          }}
          onRetryCTA={
            retryActiveOperation ?? retryBulkPublishRef.current
              ? () => {
                  if (retryActiveOperation) {
                    retryActiveOperation();
                  } else {
                    retryBulkPublishRef.current?.();
                  }
                }
              : undefined
          }
          onDoneCTA={() => {
            resetBulkPublishRef.current?.();
            resetBulkActionReportingState();
          }}
        />
      )}

      <BulkDeepPublishModalContainer
        isOpen={isBulkPublishOpen}
        setIsOpen={setIsBulkPublishOpen}
        domainEntity={domainEntity}
        environment={environment}
        selectedEntities={bulkPublishSelectedEntities}
        bulkActionReportingStore={bulkActionReportingStore}
        onBulkPublishConfirm={() => {
          setActiveReportingOperation('publish');
          setOperationStartedAt(Date.now());
        }}
        retryPublishRef={retryBulkPublishRef}
        cancelPublishRef={cancelBulkPublishRef}
        resetRef={resetBulkPublishRef}
      />
    </>
  );
};
